import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import {
    Button,
    Card,
    Col,
    Collapse,
    Container,
    Dropdown,
    DropdownItem,
    Row
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronUp,
    faList,
    faTable
} from "@fortawesome/free-solid-svg-icons";
import {
    GET_CLIENT_BUILDS,
    GET_PRODUCTS,
    GET_PROJECTS
} from "../../../graphql/queries";
import {
    BatchBuild,
    ClientBuildListData,
    ClientBuildListInput,
    DailyAnalyticsType,
    Organization,
    ProductsAdminData,
    ProjectsAdminData
} from "../../../models/types";
import { LoadingSpinner } from "../../simple/LoadingSpinner";
import styles from "../ProductsPageClient.module.scss";

const DATE_RANGE_OPTIONS: { label: string; type: DailyAnalyticsType }[] = [
    { label: "Last 7 days", type: DailyAnalyticsType.LastWeek },
    { label: "Last 30 days", type: DailyAnalyticsType.LastMonth },
    { label: "Last 90 days", type: DailyAnalyticsType.LastQuarter },
    { label: "Last 180 days", type: DailyAnalyticsType.LastHalf },
    { label: "Last 360 days", type: DailyAnalyticsType.LastYear },
    { label: "All time", type: DailyAnalyticsType.AllTime }
];

const DAYS_BY_TYPE: Partial<Record<DailyAnalyticsType, number>> = {
    [DailyAnalyticsType.LastWeek]: 7,
    [DailyAnalyticsType.LastMonth]: 30,
    [DailyAnalyticsType.LastQuarter]: 90,
    [DailyAnalyticsType.LastHalf]: 180,
    [DailyAnalyticsType.LastYear]: 365
};

const getRangeDays = (type: DailyAnalyticsType) => {
    const days = DAYS_BY_TYPE[type];
    return days === undefined ? Infinity : days;
};

const CHANGE_DEFAULT_INDEX = 0;
const BACKDROP_DEFAULT_INDEX = DATE_RANGE_OPTIONS.length - 1;

const getValidChangeOptions = (backdropType: DailyAnalyticsType) =>
    DATE_RANGE_OPTIONS.filter(
        option => getRangeDays(option.type) <= getRangeDays(backdropType)
    );

const getSimpleDate = (date: Date | string) => {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
};

const getDateRangeLabel = (type: DailyAnalyticsType) => {
    const days = DAYS_BY_TYPE[type];
    if (days === undefined) {
        return "All time";
    }

    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - days);

    return `${getSimpleDate(startDate)} - ${getSimpleDate(endDate)}`;
};

type ProjectGroup = {
    projectId: number;
    projectName: string;
    builds: BatchBuild[];
};

type ProductGroup = {
    productId: number;
    productName: string;
    projects: Map<number, ProjectGroup>;
};

type OrganizationGroup = {
    organizationId: number;
    organizationName: string;
    products: Map<number, ProductGroup>;
};

const getOrCreate = <TKey, TValue>(
    map: Map<TKey, TValue>,
    key: TKey,
    create: () => TValue
): TValue => {
    if (!map.has(key)) {
        map.set(key, create());
    }
    return map.get(key) as TValue;
};

const getOrCreateOrganizationGroup = (
    organizations: Map<number, OrganizationGroup>,
    organization: { id: number; name: string }
) =>
    getOrCreate(organizations, organization.id, () => ({
        organizationId: organization.id,
        organizationName: organization.name,
        products: new Map<number, ProductGroup>()
    }));

const getOrCreateProductGroup = (
    organizationGroup: OrganizationGroup,
    product: { id: number; name: string }
) =>
    getOrCreate(organizationGroup.products, product.id, () => ({
        productId: product.id,
        productName: product.name,
        projects: new Map<number, ProjectGroup>()
    }));

const getOrCreateProjectGroup = (
    productGroup: ProductGroup,
    project: { id: number; name: string }
) =>
    getOrCreate(productGroup.projects, project.id, () => ({
        projectId: project.id,
        projectName: project.name,
        builds: []
    }));

const groupByOrganizationProductAndProject = (
    builds: BatchBuild[] | undefined
): OrganizationGroup[] => {
    const organizations = new Map<number, OrganizationGroup>();

    for (const build of builds ? builds : []) {
        const organization = build.user?.organization;
        const product = build.revision?.project?.product;
        const project = build.revision?.project;
        if (!organization || !product || !project) {
            continue;
        }
        if (organization.name === "Seepia") {
            continue;
        }

        const organizationGroup = getOrCreateOrganizationGroup(
            organizations,
            organization
        );
        const productGroup = getOrCreateProductGroup(
            organizationGroup,
            product
        );
        const projectGroup = getOrCreateProjectGroup(productGroup, project);
        projectGroup.builds.push(build);
    }

    return Array.from(organizations.values());
};

const cloneOrganizationGroups = (
    organizationGroups: OrganizationGroup[]
): Map<number, OrganizationGroup> => {
    const organizations = new Map<number, OrganizationGroup>();
    for (const organizationGroup of organizationGroups) {
        const products = new Map<number, ProductGroup>();
        for (const productGroup of organizationGroup.products.values()) {
            products.set(productGroup.productId, {
                ...productGroup,
                projects: new Map(productGroup.projects)
            });
        }
        organizations.set(organizationGroup.organizationId, {
            ...organizationGroup,
            products
        });
    }
    return organizations;
};

const mergeCatalogIntoGroups = (
    organizationGroups: OrganizationGroup[],
    catalogProducts: ProductsAdminData["allProducts"] | undefined,
    catalogProjects: ProjectsAdminData["allProjects"] | undefined
): OrganizationGroup[] => {
    const organizations = cloneOrganizationGroups(organizationGroups);

    const organizationByProductId = new Map<number, Organization>();
    for (const product of catalogProducts ? catalogProducts : []) {
        if (product.organization && product.organization.name !== "Seepia") {
            organizationByProductId.set(product.id, product.organization);
        }
    }

    for (const project of catalogProjects ? catalogProjects : []) {
        const product = project.product;
        if (!product) {
            continue;
        }
        const organization = organizationByProductId.get(product.id);
        if (!organization) {
            continue;
        }

        const organizationGroup = getOrCreateOrganizationGroup(
            organizations,
            organization
        );
        const productGroup = getOrCreateProductGroup(
            organizationGroup,
            product
        );
        getOrCreateProjectGroup(productGroup, project);
    }

    return Array.from(organizations.values());
};

const countOrganizationProjects = (organizationGroup: OrganizationGroup) => {
    let total = 0;
    for (const productGroup of organizationGroup.products.values()) {
        total += productGroup.projects.size;
    }
    return total;
};

const countOrganizationBuilds = (organizationGroup: OrganizationGroup) => {
    let total = 0;
    for (const productGroup of organizationGroup.products.values()) {
        for (const projectGroup of productGroup.projects.values()) {
            total += projectGroup.builds.length;
        }
    }
    return total;
};

const countOrganizationBuildsById = (
    organizationGroups: OrganizationGroup[]
) => {
    const counts = new Map<number, number>();
    for (const organizationGroup of organizationGroups) {
        counts.set(
            organizationGroup.organizationId,
            countOrganizationBuilds(organizationGroup)
        );
    }
    return counts;
};

const countProjectBuildsById = (organizationGroups: OrganizationGroup[]) => {
    const counts = new Map<number, number>();
    for (const organizationGroup of organizationGroups) {
        for (const productGroup of organizationGroup.products.values()) {
            for (const projectGroup of productGroup.projects.values()) {
                counts.set(projectGroup.projectId, projectGroup.builds.length);
            }
        }
    }
    return counts;
};

const countFor = (counts: Map<number, number>, id: number) =>
    counts.has(id) ? (counts.get(id) as number) : 0;

const formatCount = (backdropCount: number, changeCount: number) =>
    `${backdropCount} (+${changeCount})`;

const formatProjectCount = (count: number) =>
    `${count} ${count === 1 ? "project" : "projects"}`;

const formatBuildCount = (count: number) =>
    `${count} ${count === 1 ? "build" : "builds"}`;

const getBuildDisplayName = (build: BatchBuild) =>
    build.name ? build.name : `Build ${build.id}`;

const buildLink = (build: BatchBuild) => {
    const productId = build.revision?.project?.product?.id;
    const projectId = build.revision?.project?.id;
    const revisionId = build.revision?.id;
    if (!productId || !projectId || !revisionId) {
        return undefined;
    }
    return `/products/${productId}/projects/${projectId}/revisions/${revisionId}/history/${build.id}`;
};

const BuildRow = ({ build }: { build: BatchBuild }) => {
    const link = buildLink(build);
    const displayName = getBuildDisplayName(build);
    const nameProps = {
        title: displayName,
        className: "d-inline-block text-truncate",
        style: { maxWidth: "100%" }
    };

    return (
        <div
            key={build.id}
            className="list-row d-flex align-items-center px-2 py-1 gap-2"
        >
            <span className="text-subtle" style={{ flex: "1 1 0" }}>
                {build.user ? build.user.displayName : "Unknown user"}
            </span>
            <span
                className="analytics-font mx-2 text-subtle text-center flex-shrink-0"
                style={{ flexGrow: 0 }}
            >
                {getSimpleDate(build.dateCreated)}
            </span>
            <span className="text-end" style={{ flex: "2 1 0", minWidth: 0 }}>
                {link ? (
                    <Link to={link} {...nameProps}>
                        {displayName}
                    </Link>
                ) : (
                    <span {...nameProps}>{displayName}</span>
                )}
            </span>
        </div>
    );
};

const ClientBuildStatisticsImplementation = () => {
    const [selectedBackdrop, setSelectedBackdrop] = useState(
        DATE_RANGE_OPTIONS[BACKDROP_DEFAULT_INDEX]
    );
    const [selectedChange, setSelectedChange] = useState(
        DATE_RANGE_OPTIONS[CHANGE_DEFAULT_INDEX]
    );
    const [appliedBackdrop, setAppliedBackdrop] = useState(
        DATE_RANGE_OPTIONS[BACKDROP_DEFAULT_INDEX]
    );
    const [appliedChange, setAppliedChange] = useState(
        DATE_RANGE_OPTIONS[CHANGE_DEFAULT_INDEX]
    );
    const [expandedProjects, setExpandedProjects] = useState<Set<number>>(
        new Set()
    );
    const [collapsedOrganizations, setCollapsedOrganizations] = useState<
        Set<number>
    >(new Set());
    const [isCondensedView, setIsCondensedView] = useState(false);

    const validChangeOptions = useMemo(
        () => getValidChangeOptions(selectedBackdrop.type),
        [selectedBackdrop]
    );

    const handleSelectBackdrop = (
        option: (typeof DATE_RANGE_OPTIONS)[number]
    ) => {
        setSelectedBackdrop(option);
        const stillValid = getValidChangeOptions(option.type);
        if (!stillValid.some(o => o.type === selectedChange.type)) {
            setSelectedChange(stillValid[stillValid.length - 1]);
        }
    };

    const toggleProject = (projectId: number) => {
        setExpandedProjects(previous => {
            const next = new Set(previous);
            if (next.has(projectId)) {
                next.delete(projectId);
            } else {
                next.add(projectId);
            }
            return next;
        });
    };

    const toggleOrganization = (organizationId: number) => {
        setCollapsedOrganizations(previous => {
            const next = new Set(previous);
            if (next.has(organizationId)) {
                next.delete(organizationId);
            } else {
                next.add(organizationId);
            }
            return next;
        });
    };

    const isChangeSameAsBackdrop = appliedChange.type === appliedBackdrop.type;

    const {
        data: { getClientBuilds: getBackdropClientBuilds } = {},
        loading: backdropLoading,
        error: backdropError
    } = useQuery<ClientBuildListData, ClientBuildListInput>(GET_CLIENT_BUILDS, {
        variables: {
            input: {
                dailyBuildsType: appliedBackdrop.type
            }
        }
    });

    const {
        data: { getClientBuilds: getChangeClientBuilds } = {},
        loading: changeLoading,
        error: changeError
    } = useQuery<ClientBuildListData, ClientBuildListInput>(GET_CLIENT_BUILDS, {
        variables: {
            input: {
                dailyBuildsType: appliedChange.type
            }
        },
        skip: isChangeSameAsBackdrop
    });

    const {
        data: { allProducts: catalogProducts } = {},
        loading: catalogProductsLoading,
        error: catalogProductsError
    } = useQuery<ProductsAdminData>(GET_PRODUCTS, {
        variables: { organizationId: "1" }
    });

    const {
        data: { allProjects: catalogProjects } = {},
        loading: catalogProjectsLoading,
        error: catalogProjectsError
    } = useQuery<ProjectsAdminData>(GET_PROJECTS, {
        variables: { organizationId: "1" }
    });

    const backdropOrganizationGroupsFromBuilds = useMemo(
        () => groupByOrganizationProductAndProject(getBackdropClientBuilds),
        [getBackdropClientBuilds]
    );

    const changeOrganizationGroupsFromBuilds = useMemo(
        () =>
            isChangeSameAsBackdrop
                ? backdropOrganizationGroupsFromBuilds
                : groupByOrganizationProductAndProject(getChangeClientBuilds),
        [
            isChangeSameAsBackdrop,
            backdropOrganizationGroupsFromBuilds,
            getChangeClientBuilds
        ]
    );

    const backdropOrganizationGroups = useMemo(
        () =>
            mergeCatalogIntoGroups(
                backdropOrganizationGroupsFromBuilds,
                catalogProducts,
                catalogProjects
            ),
        [backdropOrganizationGroupsFromBuilds, catalogProducts, catalogProjects]
    );

    const changeOrganizationGroups = useMemo(
        () =>
            isChangeSameAsBackdrop
                ? backdropOrganizationGroups
                : mergeCatalogIntoGroups(
                      changeOrganizationGroupsFromBuilds,
                      catalogProducts,
                      catalogProjects
                  ),
        [
            isChangeSameAsBackdrop,
            backdropOrganizationGroups,
            changeOrganizationGroupsFromBuilds,
            catalogProducts,
            catalogProjects
        ]
    );

    const backdropOrganizationCounts = useMemo(
        () => countOrganizationBuildsById(backdropOrganizationGroups),
        [backdropOrganizationGroups]
    );

    const backdropProjectCounts = useMemo(
        () => countProjectBuildsById(backdropOrganizationGroups),
        [backdropOrganizationGroups]
    );

    const changeOrganizationCounts = useMemo(
        () => countOrganizationBuildsById(changeOrganizationGroups),
        [changeOrganizationGroups]
    );

    const changeProjectCounts = useMemo(
        () => countProjectBuildsById(changeOrganizationGroups),
        [changeOrganizationGroups]
    );

    const loading =
        backdropLoading ||
        (!isChangeSameAsBackdrop && changeLoading) ||
        catalogProductsLoading ||
        catalogProjectsLoading;
    const error =
        backdropError ||
        (!isChangeSameAsBackdrop && changeError) ||
        catalogProductsError ||
        catalogProjectsError;

    return (
        <Container className="p-4 pageMaxWidthClient">
            <Row className="justify-content-center">
                <Col
                    xs={12}
                    md={11}
                    lg={10}
                    xl={9}
                    className="text-subtle mt-1"
                >
                    <h2 className={styles.listTitle}>
                        Client build job statistics
                    </h2>
                    <div className="d-flex flex-wrap align-items-center ms-2 mt-4 gap-2">
                        <Dropdown>
                            <Dropdown.Toggle variant="secondary" size="sm">
                                Data in {selectedBackdrop.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {DATE_RANGE_OPTIONS.map(option => (
                                    <DropdownItem
                                        key={option.label}
                                        active={
                                            option.label ===
                                            selectedBackdrop.label
                                        }
                                        onClick={() =>
                                            handleSelectBackdrop(option)
                                        }
                                    >
                                        {option.label}
                                    </DropdownItem>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle variant="secondary" size="sm">
                                Change in {selectedChange.label}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {validChangeOptions.map(option => (
                                    <DropdownItem
                                        key={option.label}
                                        active={
                                            option.label ===
                                            selectedChange.label
                                        }
                                        onClick={() =>
                                            setSelectedChange(option)
                                        }
                                    >
                                        {option.label}
                                    </DropdownItem>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Button
                            variant="primary"
                            size="sm"
                            className="mx-2"
                            onClick={() => {
                                setAppliedBackdrop(selectedBackdrop);
                                setAppliedChange(selectedChange);
                            }}
                        >
                            Update
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            className="ms-auto me-2 text-end"
                            onClick={() =>
                                setIsCondensedView(previous => !previous)
                            }
                        >
                            <FontAwesomeIcon
                                icon={isCondensedView ? faList : faTable}
                                className="me-1"
                            />
                            {isCondensedView
                                ? "Detailed view"
                                : "Condensed view"}
                        </Button>
                    </div>
                    <div className="text-subtle my-3 bread-text text-end ms-auto">
                        Showing data from:{" "}
                        {getDateRangeLabel(appliedBackdrop.type)} (change:{" "}
                        {getDateRangeLabel(appliedChange.type)})
                    </div>

                    {loading && (
                        <LoadingSpinner header="Loading build statistics" />
                    )}

                    {error && (
                        <div className="text-subtle p-3 text-center">
                            Failed to load client build statistics.
                        </div>
                    )}

                    {!loading && !error && isCondensedView && (
                        <div className="p-2 text-contrast">
                            {backdropOrganizationGroups.map(
                                organizationGroup => (
                                    <div
                                        key={organizationGroup.organizationId}
                                        className="mb-2"
                                    >
                                        <div>
                                            <span className="fw-bold me-2">
                                                {
                                                    organizationGroup.organizationName
                                                }{" "}
                                            </span>
                                            (
                                            {formatProjectCount(
                                                countOrganizationProjects(
                                                    organizationGroup
                                                )
                                            )}
                                            )
                                        </div>
                                        <ul className="mb-0 ps-4">
                                            {Array.from(
                                                organizationGroup.products.values()
                                            ).map(productGroup => (
                                                <li
                                                    key={productGroup.productId}
                                                >
                                                    {productGroup.productName}
                                                    <ul className="ps-4">
                                                        {Array.from(
                                                            productGroup.projects.values()
                                                        ).map(projectGroup => (
                                                            <li
                                                                key={
                                                                    projectGroup.projectId
                                                                }
                                                            >
                                                                {
                                                                    projectGroup.projectName
                                                                }{" "}
                                                                -{" "}
                                                                {formatBuildCount(
                                                                    countFor(
                                                                        backdropProjectCounts,
                                                                        projectGroup.projectId
                                                                    )
                                                                )}
                                                                <span className="ms-2">
                                                                    (+
                                                                    {countFor(
                                                                        changeProjectCounts,
                                                                        projectGroup.projectId
                                                                    )}
                                                                    )
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )
                            )}
                        </div>
                    )}

                    {!loading && !error && !isCondensedView && (
                        <>
                            {backdropOrganizationGroups.map(
                                organizationGroup => {
                                    const isOrganizationExpanded =
                                        !collapsedOrganizations.has(
                                            organizationGroup.organizationId
                                        );
                                    return (
                                        <Card
                                            key={
                                                organizationGroup.organizationId
                                            }
                                            className="mb-3"
                                        >
                                            <Card.Header
                                                className="d-flex align-items-center mouseHover"
                                                onClick={() =>
                                                    toggleOrganization(
                                                        organizationGroup.organizationId
                                                    )
                                                }
                                            >
                                                <span className="text-subtle text-lg fw-bold flex-grow-1">
                                                    <h5 className="d-inline mb-0">
                                                        {
                                                            organizationGroup.organizationName
                                                        }
                                                    </h5>
                                                    <FontAwesomeIcon
                                                        icon={
                                                            isOrganizationExpanded
                                                                ? faChevronUp
                                                                : faChevronDown
                                                        }
                                                        className="mx-2"
                                                        size="xs"
                                                    />
                                                </span>
                                                <span className="me-2 fw-bold text-subtle">
                                                    {formatCount(
                                                        countFor(
                                                            backdropOrganizationCounts,
                                                            organizationGroup.organizationId
                                                        ),
                                                        countFor(
                                                            changeOrganizationCounts,
                                                            organizationGroup.organizationId
                                                        )
                                                    )}
                                                </span>
                                            </Card.Header>
                                            <Collapse
                                                in={isOrganizationExpanded}
                                            >
                                                <div>
                                                    <Card.Body>
                                                        {Array.from(
                                                            organizationGroup.products.values()
                                                        ).map(productGroup => (
                                                            <div
                                                                key={
                                                                    productGroup.productId
                                                                }
                                                                className="mb-3"
                                                            >
                                                                <div className="text-subtle fw-bold ms-2 mb-2">
                                                                    {
                                                                        productGroup.productName
                                                                    }
                                                                </div>
                                                                {Array.from(
                                                                    productGroup.projects.values()
                                                                ).map(
                                                                    projectGroup => {
                                                                        const isExpanded =
                                                                            expandedProjects.has(
                                                                                projectGroup.projectId
                                                                            );
                                                                        return (
                                                                            <Card
                                                                                key={
                                                                                    projectGroup.projectId
                                                                                }
                                                                                className="mb-2"
                                                                            >
                                                                                <Card.Header
                                                                                    className="d-flex align-items-center mouseHover"
                                                                                    onClick={() =>
                                                                                        toggleProject(
                                                                                            projectGroup.projectId
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <span className="flex-grow-1">
                                                                                        {
                                                                                            projectGroup.projectName
                                                                                        }
                                                                                        <FontAwesomeIcon
                                                                                            icon={
                                                                                                isExpanded
                                                                                                    ? faChevronUp
                                                                                                    : faChevronDown
                                                                                            }
                                                                                            className="mx-2"
                                                                                            size="xs"
                                                                                        />
                                                                                    </span>
                                                                                    <span className="me-2">
                                                                                        {formatCount(
                                                                                            countFor(
                                                                                                backdropProjectCounts,
                                                                                                projectGroup.projectId
                                                                                            ),
                                                                                            countFor(
                                                                                                changeProjectCounts,
                                                                                                projectGroup.projectId
                                                                                            )
                                                                                        )}
                                                                                    </span>
                                                                                </Card.Header>
                                                                                <Collapse
                                                                                    in={
                                                                                        isExpanded
                                                                                    }
                                                                                >
                                                                                    <div>
                                                                                        <Card.Body className="align-items-center p-2">
                                                                                            {projectGroup.builds.map(
                                                                                                build => (
                                                                                                    <BuildRow
                                                                                                        key={
                                                                                                            build.id
                                                                                                        }
                                                                                                        build={
                                                                                                            build
                                                                                                        }
                                                                                                    />
                                                                                                )
                                                                                            )}
                                                                                        </Card.Body>
                                                                                    </div>
                                                                                </Collapse>
                                                                            </Card>
                                                                        );
                                                                    }
                                                                )}
                                                            </div>
                                                        ))}
                                                    </Card.Body>
                                                </div>
                                            </Collapse>
                                        </Card>
                                    );
                                }
                            )}
                        </>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export const ClientBuildStatistics = ClientBuildStatisticsImplementation;
